import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Link, Typography } from '@mui/material';

import MicrositeImages from '~/components/atoms/microsite-images/MicrositeImages';
import { modelMicrositeService } from '~/components/organisms/previewBar/model-data';
import { QR_CODE_BUILDER_CREATED_ID_PARAM_NAME } from '~/constants/qr-code';
import { useAppDispatch } from '~/hooks/useRedux';
import { imagesSlice } from '~/store/reducers/previewScreens/images';
import { getImagesReducer } from '~/store/reducers/previewScreens/images/selectors';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { COLORS } from '~/styles/colors';
import { mainPreviewContainerStyles } from '~/styles/common';

type ImagesPreviewScreenProps = {
  isMyMicrosite?: boolean;
  stopNetworkLoading?: boolean;
};

const { actions } = imagesSlice;

export const ImagesPreviewScreen = ({ stopNetworkLoading, isMyMicrosite }: ImagesPreviewScreenProps) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const createdId = searchParams.get(QR_CODE_BUILDER_CREATED_ID_PARAM_NAME) || null;

  const qrCodeState = useSelector(getQrCodeReducerData);
  const imagesState = useSelector(getImagesReducer);
  const {
    bacgkroundColor,
    fonts: { title, text },
    imageInformation: { header, description, website },
    images,
    imagesType,
    buttons,
    micrositeStyles,
  } = imagesState;

  useEffect(() => {
    if (stopNetworkLoading) {
      return;
    }
    if (!createdId) {
      modelMicrositeService.images(imagesState, qrCodeState, { dispatch, actions });
    }
  }, []);

  return (
    <Box
      sx={[
        {
          ...mainPreviewContainerStyles,
          alignItems: 'normal',
          ...micrositeStyles?.container,
        },
        isMyMicrosite && { margin: 0, borderRadius: 0 },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          textAlign: 'center',
          minWidth: '100%',
        }}
      >
        <Box
          sx={{
            pt: 4,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              width: '503px',
              height: '503px',
              backgroundColor: `${bacgkroundColor[0]}`,
              borderRadius: '50%',
              bottom: -32,
              left: '50%',
              transform: 'translateX(-50% )',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              mb: 3,
              fontFamily: title,
              pl: 2,
              pr: 2,
              wordBreak: 'break-word',
              position: 'relative',
              zIndex: 1,
              color: COLORS.white,
            }}
          >
            {header}
          </Typography>
          <Typography
            sx={{
              fontSize: 9,
              fontFamily: text,
              pl: 2,
              pr: 2,
              mb: 2,
              wordBreak: 'break-word',
              position: 'relative',
              zIndex: 1,
              color: COLORS.white,
            }}
          >
            {description}
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{
              position: 'relative',
              zIndex: 1,
            }}
          >
            {buttons
              .filter((item) => item.text !== '')
              .map((item) => {
                return (
                  <Button
                    key={item.id}
                    target="_blank"
                    href={item.url}
                    variant="outlined"
                    sx={{
                      maxWidth: 160,
                      m: 1,
                      color: COLORS.white,
                      border: `1px solid ${COLORS.white}`,
                      wordBreak: 'break-word',
                      borderRadius: '100px',
                      minHeight: '28px',
                      fontSize: '9px',
                      width: '100%',
                      '&:hover': {
                        border: `1px solid ${COLORS.white}`,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
          </Box>
        </Box>
        <Box p={2}>
          <MicrositeImages data={images} type={imagesType} />
        </Box>
        <Link
          href={website}
          sx={{
            fontSize: 11,
            mt: 3,
            paddingBottom: 2,
            color: COLORS.purple,
            textDecoration: 'none',
            fontFamily: text,
            pl: 2,
            pr: 2,
            wordBreak: 'break-word',
          }}
        >
          {website}
        </Link>
      </Box>
    </Box>
  );
};
